import { Box, Typography } from '@mui/material';
import type { PortableTextBlock, PortableTextReactComponents } from '@portabletext/react';
import { PortableText as PortableTextReact } from '@portabletext/react';
import type { FC, PropsWithChildren } from 'react';
import { Link } from '~/components/atoms/Link';
import { IconsWithTextVertical } from '~/components/molecules/IconsTextVertical';
import { LoyaltyTierColumns } from '~/components/molecules/LoyaltyTierColumns/LoyaltyTierColumns';
import type { IconsTextVertical } from '~/types/sanity-typegen';
import type { SanityLoyaltyTiers } from '~/types/sanity/loyalty';
import type { TestIdProps } from '~/types/testId';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

import type { RichTextHeadingLevel } from './PortableText.styles';
import { richTextBlockquoteStyles, richTextHeadingStyles } from './PortableText.styles';

type PortableTextProps = { portableText: PortableTextBlock[] } & TestIdProps;

const RichTextHeading: FC<PropsWithChildren<{ level: RichTextHeadingLevel }>> = ({ level, children }) => (
  <Typography
    component={level}
    {...richTextHeadingStyles[level]}
    sx={{
      '&:first-of-type': {
        marginTop: 0,
      },
    }}
  >
    {children}
  </Typography>
);

/**
 * Map Sanity `BlockContent` elements to React components.
 * Pass in a custom component or leave it as is to use the default block.
 *
 *
 * @see https://www.sanity.io/docs/portable-text-to-react
 */
export const PortableText: FC<PortableTextProps> = ({ portableText, testId }) => {
  const portableTextComponents: Partial<PortableTextReactComponents> = {
    block: {
      normal: ({ children }) => <Typography variant="body1">{children}</Typography>,
      h1: ({ children }) => <RichTextHeading level="h1">{children}</RichTextHeading>,
      h2: ({ children }) => <RichTextHeading level="h2">{children}</RichTextHeading>,
      h3: ({ children }) => <RichTextHeading level="h3">{children}</RichTextHeading>,
      h4: ({ children }) => <RichTextHeading level="h4">{children}</RichTextHeading>,
      h5: ({ children }) => <RichTextHeading level="h5">{children}</RichTextHeading>,
      h6: ({ children }) => <RichTextHeading level="h6">{children}</RichTextHeading>,
      blockquote: ({ children }) => (
        <Typography component="blockquote" variant="body1" sx={richTextBlockquoteStyles}>
          {children}
        </Typography>
      ),
    },
    marks: {
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      sup: ({ children }) => <sup>{children}</sup>,
      link: ({ value, children }) => <Link to={value.href}>{children}</Link>,
    },
    listItem: {
      number: ({ children }) => <Typography component="li">{children}</Typography>,
      bullet: ({ children }) => <Typography component="li">{children}</Typography>,
    },
    types: {
      image: ({ value }) => {
        const {
          metadata: {
            dimensions: { width },
          },
        } = value ?? {};

        return (
          <Box
            component="img"
            src={value.url}
            alt={value.alt ?? ''}
            display="block"
            width={width}
            height="auto"
            maxWidth="100%"
            mx="auto"
            my={2}
          />
        );
      },
      iconsTextVertical: ({ value }: { value: IconsTextVertical }) => (
        <IconsWithTextVertical iconsWithText={value.iconsTextVerticalArray} />
      ),
      loyaltyTiers: ({ value }: { value: SanityLoyaltyTiers }) => (
        <LoyaltyTierColumns loyaltyTiersArray={value.loyaltyTiersArray} />
      ),
    },
  };

  const compoundTestIdGenerator = getCompoundTestIdGenerator('portableText', testId);

  return (
    <Box
      sx={{
        '& > p:not(:last-child), & > div:not(:last-child)': { marginBottom: (theme) => theme.spacing(2) },
      }}
      data-testid={compoundTestIdGenerator()}
    >
      <PortableTextReact value={portableText} components={portableTextComponents} />
    </Box>
  );
};
